import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, type RealtimeManager } from '@/shared/realtime';

import { groupKeys } from './api';
import { markGroupDeleted } from './lib/deleted';

/** Realtime event names of the groups module (backend `GroupRealtime`). */
export const GROUP_EVENTS = {
  groupUpdated: 'groups.group-updated',
  groupArchived: 'groups.group-archived',
  groupDeleted: 'groups.group-deleted',
  membersChanged: 'groups.members-changed',
  membershipChanged: 'groups.membership-changed',
  joinRequestsChanged: 'groups.join-requests-changed',
  invitationsChanged: 'groups.invitations-changed',
} as const;

interface GroupPayload {
  groupId?: string;
}

/** Which cached queries an event makes stale (pure, unit tested). */
export function staleKeysFor(type: string, groupId: string | undefined): (readonly unknown[])[] {
  const keys: (readonly unknown[])[] = [];
  const group = (key: (id: string) => readonly unknown[]) => {
    if (groupId) keys.push(key(groupId));
  };
  switch (type) {
    case GROUP_EVENTS.groupUpdated:
    case GROUP_EVENTS.groupArchived:
      group(groupKeys.detail);
      keys.push(groupKeys.lists(), [...groupKeys.all, 'search'], groupKeys.counters, groupKeys.myInvitations);
      break;
    case GROUP_EVENTS.groupDeleted:
      keys.push(groupKeys.lists(), [...groupKeys.all, 'search'], groupKeys.counters, groupKeys.myInvitations);
      break;
    case GROUP_EVENTS.membersChanged:
      group(groupKeys.detail);
      group(groupKeys.members);
      keys.push(groupKeys.lists());
      break;
    case GROUP_EVENTS.membershipChanged:
      group(groupKeys.detail);
      group(groupKeys.myRole);
      keys.push(groupKeys.lists(), [...groupKeys.all, 'search'], groupKeys.counters);
      break;
    case GROUP_EVENTS.joinRequestsChanged:
      group(groupKeys.detail);
      group(groupKeys.joinRequests);
      keys.push(groupKeys.counters, groupKeys.lists());
      break;
    case GROUP_EVENTS.invitationsChanged:
      group(groupKeys.detail);
      group(groupKeys.invitations);
      keys.push(groupKeys.counters, groupKeys.myInvitations, groupKeys.lists());
      break;
    default:
      break;
  }
  return keys;
}

/**
 * Keeps the groups caches live: every `groups.*` event (topic `group:<id>` on group pages, or addressed to the user —
 * own membership, invitations, join requests to decide) invalidates what it affects; after a reconnect everything is
 * refetched because events may have been missed.
 */
export function registerGroupRealtime(realtime: RealtimeManager, queryClient: QueryClient): () => void {
  const offEvents = realtime.on<GroupPayload | null>('groups.*', (payload, envelope) => {
    if (envelope.type === GROUP_EVENTS.groupDeleted && payload?.groupId) markGroupDeleted(payload.groupId);
    for (const queryKey of staleKeysFor(envelope.type, payload?.groupId)) {
      void queryClient.invalidateQueries({ queryKey });
    }
  });
  const offReconnect = realtime.on(REALTIME_RECONNECTED, () => {
    void queryClient.invalidateQueries({ queryKey: groupKeys.all });
  });
  return () => {
    offEvents();
    offReconnect();
  };
}
